/**
 * ChartViewManager — Per-chart view switching service
 *
 * Manages registration, switching, and lifecycle of chart views.
 * Each TabChartRenderer owns one instance.
 *
 * Registered views implement IChartView (HoraClockView, RashiChakraView).
 * The default 'chakra' view is composite (all standard SVG layers) and
 * handled internally via restoreDefaultChakraLayers().
 */

import * as d3 from 'd3';
import { EventStream } from '../core/EventStream';
import { isEventForChart } from '../helpers/ChakraEventHelper';
import { devWarn } from '../../../../../utils/devLog';
import type { ChartViewMode } from '../../../../../types/ChartViewTypes';
import { SPECIAL_VIEW_GROUPS } from '../../../../../types/ChartViewTypes';
import type { EventSubscription } from '../types/EventTypes';

/** Interface for pluggable chart view renderers */
export interface IChartView {
    readonly viewId: ChartViewMode;
    activate(options?: Record<string, any>): Promise<void>;
    deactivate(): void;
    destroy(): void;
    isRendered(): boolean;
}

export class ChartViewManager {
    private chartId: string;
    private eventStream: EventStream;
    private views: Map<ChartViewMode, IChartView> = new Map();
    private activeView: ChartViewMode = 'chakra';
    private onChakraRestore: (() => void) | null = null;
    private subscriptions: EventSubscription[] = [];

    constructor(chartId: string, eventStream: EventStream) {
        this.chartId = chartId;
        this.eventStream = eventStream;
        this.setupEventSubscriptions();
    }

    /** Register a view adapter */
    registerView(view: IChartView): void {
        this.views.set(view.viewId, view);
    }

    /** Set callback for restoring chakra overlay layer visibility when returning to 'chakra' view */
    setChakraRestoreCallback(cb: () => void): void {
        this.onChakraRestore = cb;
    }

    /** Current active view mode */
    getActiveView(): ChartViewMode {
        return this.activeView;
    }

    /** Switch to target view. No-op if already active. */
    async switchView(targetView: ChartViewMode, options?: Record<string, any>): Promise<void> {
        if (targetView === this.activeView) return;

        const chartContent = this.getChartContent();
        if (!chartContent) return;

        // 1. Deactivate current non-chakra view
        if (this.activeView !== 'chakra') {
            const currentView = this.views.get(this.activeView);
            currentView?.deactivate();
        }

        // 2. Hide ALL <g> children (universal reset)
        chartContent.selectAll(':scope > g').each(function () {
            d3.select(this).style('display', 'none');
        });

        // 3. Update state
        this.activeView = targetView;

        // 4. Activate target
        if (targetView === 'chakra') {
            this.restoreDefaultChakraLayers(chartContent);
        } else {
            const view = this.views.get(targetView);
            if (view) {
                await view.activate(options);
            } else {
                devWarn(`ChartViewManager[${this.chartId}]: No adapter registered for '${targetView}'`);
            }
        }
    }

    /** Restore active view from saved settings (called after renderChart) */
    async restoreFromSettings(savedView: ChartViewMode): Promise<void> {
        if (savedView === 'chakra') return;

        if (this.activeView === savedView) {
            // Already on this view — re-render with updated data (snapshot switch)
            const view = this.views.get(savedView);
            if (view) {
                await view.activate();
            }
        } else {
            await this.switchView(savedView);
        }
    }

    /** Re-render current view (e.g. after snapshot switch while on non-chakra view) */
    async refreshCurrentView(options?: Record<string, any>): Promise<void> {
        if (this.activeView === 'chakra') return;
        const view = this.views.get(this.activeView);
        if (view) {
            await view.activate(options);
        }
    }

    /** Cleanup all registered views and subscriptions */
    destroy(): void {
        for (const view of this.views.values()) {
            view.destroy();
        }
        this.views.clear();
        this.activeView = 'chakra';
        this.onChakraRestore = null;
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
        this.subscriptions = [];
    }

    // --- Private ---

    private setupEventSubscriptions(): void {
        // Unified view switch
        this.subscriptions.push(
            this.eventStream.subscribe('chart.view.switch', (event: any) => {
                if (isEventForChart(event, this.chartId)) {
                    const targetView = (event.data?.view as ChartViewMode) || 'chakra';
                    this.switchView(targetView, event.data).catch(err =>
                        devWarn(`ChartViewManager[${this.chartId}]: switchView failed`, err)
                    );
                }
            })
        );

        // Hora Clock design changed — forward to active view
        this.subscriptions.push(
            this.eventStream.subscribe('hora.clock.design.changed', (event: any) => {
                if (isEventForChart(event, this.chartId) && this.activeView === 'horaClock') {
                    const horaView = this.views.get('horaClock');
                    horaView?.activate({ designOverride: event.data?.values })?.catch(err =>
                        devWarn(`ChartViewManager[${this.chartId}]: hora design update failed`, err)
                    );
                }
            })
        );

        // Hora Clock 12/24 hour mode changed
        this.subscriptions.push(
            this.eventStream.subscribe('hora.clock.mode.changed', (event: any) => {
                if (isEventForChart(event, this.chartId) && this.activeView === 'horaClock') {
                    const horaView = this.views.get('horaClock') as any;
                    const newMode = event.data?.hourMode;
                    if (horaView?.setHourMode && (newMode === 12 || newMode === 24)) {
                        horaView.setHourMode(newMode);
                        // Persist to settings
                        try {
                            const ctx = window.multipleChartsContext;
                            if (ctx?.updateChart) {
                                ctx.updateChart(this.chartId, {
                                    settings: { horaClockHourMode: newMode }
                                } as any);
                            }
                        } catch { /* ignore */ }
                        // Re-render with new mode
                        horaView.activate()?.catch((err: any) =>
                            devWarn(`ChartViewManager[${this.chartId}]: hora mode switch failed`, err)
                        );
                    }
                }
            })
        );
    }

    /** Show all standard layers except special view groups, then restore individual overlay visibility */
    private restoreDefaultChakraLayers(
        chartContent: d3.Selection<any, unknown, HTMLElement, any>
    ): void {
        chartContent.selectAll(':scope > g').each(function () {
            const el = d3.select(this);
            const isSpecial = SPECIAL_VIEW_GROUPS.some(cls => el.classed(cls));
            if (!isSpecial) {
                el.style('display', null);
            }
        });
        this.onChakraRestore?.();
    }

    private getChartContent(): d3.Selection<any, unknown, HTMLElement, any> | null {
        const sel = d3.select(`#viewport-content-${this.chartId}`);
        return sel.empty() ? null : sel;
    }
}
